/**
 * @fileoverview Priority lookup binding a `ViolationMapping` to the correct
 *   logged violation.
 * @module inspection-renderer/violations
 *
 * Responsibilities:
 * - Resolve which `LoggedViolation` (if any) belongs to a given mapping on a
 *   field, using a strict priority: exact code+codeSetId match first, then —
 *   only when unambiguous — legacy fallbacks for records written before
 *   `codeSetId` existed and for codeless `source: 'question'` violations.
 * - Expose `UNMAPPED_FAILURE_CODE_SET_ID`, the synthetic codeSetId stamped on
 *   pass/fail fields that fail without any bound violation code.
 *
 * Dependencies:
 * - `LoggedViolation` (renderer-local types).
 * - `ViolationMapping` (`@firesquirrel/core-domain`).
 *
 * Architecture:
 * - Pure functions, no state. Consumed by `<InspectionRenderer>`'s
 *   `ViolationsCaptureContextValue` (`getViolation` / `setViolation`) so every
 *   read/update/remove goes through the same priority rule.
 *
 * Notes:
 * - The legacy fallbacks exist for records persisted before codeSetId was
 *   captured (commit 563cb5f5d). They are intentionally gated by
 *   `allowsLegacyViolationFallback` — when a field has more than one bound
 *   mapping, a code-only or codeless match is ambiguous and MUST NOT be used,
 *   otherwise one mapping reads/updates/removes another mapping's violation.
 */

import type { ViolationMapping } from '@firesquirrel/core-domain';
import type { LoggedViolation } from './types';

/**
 * Synthetic `codeSetId` used by `getUnmappedFailureMapping` for pass/fail
 * fields that have no bound violation code. The mapping's `code` is
 * `field:<fieldId>`; both values are metadata only — the pass/fail state
 * itself is the trigger.
 *
 * @contract UNMAPPED_FAILURE_CODE_SET_ID
 * Purpose: sentinel distinguishing synthetic unmapped-failure mappings from
 * real code-set mappings.
 * Fields: constant string `'__field_failure__'`.
 * Rules:   never collides with a real codeSetId (code sets use registry ids);
 *          do not persist it as if it were a real code set.
 * Sync:    renderer-local; adapters forward it verbatim inside `codes[]`.
 */
export const UNMAPPED_FAILURE_CODE_SET_ID = '__field_failure__';

/**
 * True when the legacy fallbacks may be used for this mapping.
 *
 * - A bound mapping may fall back only when it is the field's ONLY bound
 *   mapping (`boundMappingCount === 1`) — with two or more bound mappings a
 *   code-only or codeless match is ambiguous and would let mapping B claim
 *   mapping A's violation.
 * - The synthetic unmapped-failure mapping (`codeSetId ===
 *   UNMAPPED_FAILURE_CODE_SET_ID`) may fall back only when the field has NO
 *   bound mappings (`boundMappingCount === 0`) — it is the sole owner of the
 *   codeless `source: 'question'` record.
 *
 * @param mapping the mapping being looked up (only `codeSetId` is inspected).
 * @param boundMappingCount number of bound mappings (`bindingMode !==
 *   'pending-tenant-bind'`) on the field across all rendered forms.
 */
export function allowsLegacyViolationFallback(
  mapping: Pick<ViolationMapping, 'codeSetId'>,
  boundMappingCount: number,
): boolean {
  if (mapping.codeSetId === UNMAPPED_FAILURE_CODE_SET_ID) {
    return boundMappingCount === 0;
  }
  return boundMappingCount === 1;
}

/**
 * Find the logged violation bound to `mapping` on `fieldId`, using three
 * strictly-ordered passes over the violations on that field:
 *
 * 1. Exact match — `code === mapping.code` AND
 *    `String(codeSetId ?? '') === String(mapping.codeSetId ?? '')`.
 * 2. Legacy code-only match — `code === mapping.code` regardless of
 *    codeSetId. Only when `allowLegacyFallback`.
 * 3. Legacy codeless question violation — `codes.length === 0 &&
 *    source === 'question'` (codeless failed-toggle records written by the
 *    inspectionFieldResponses adapter). Only when `allowLegacyFallback`.
 *
 * Each tier is a separate pass over the field's violations so a tier-1 match
 * on a later record always beats a tier-2/3 match on an earlier one; the
 * fallbacks are never consulted unless `allowLegacyFallback` is true.
 * Returns null when nothing matches.
 *
 * @param violations all logged violations (any field); filtered internally.
 * @param fieldId the field whose violations are searched.
 * @param mapping the mapping to bind (`code` and `codeSetId` inspected).
 * @param allowLegacyFallback result of `allowsLegacyViolationFallback`.
 */
export function findViolationForMapping(
  violations: readonly LoggedViolation[],
  fieldId: string,
  mapping: Pick<ViolationMapping, 'code' | 'codeSetId'>,
  allowLegacyFallback: boolean,
): LoggedViolation | null {
  const onField = violations.filter((v) => v.fieldId === fieldId);
  const mappingCodeSetId = String(mapping.codeSetId ?? '');

  const exact = onField.find((v) =>
    v.codes.some((c) => c.code === mapping.code && String(c.codeSetId ?? '') === mappingCodeSetId),
  );
  if (exact) return exact;

  if (!allowLegacyFallback) return null;

  const codeOnly = onField.find((v) => v.codes.some((c) => c.code === mapping.code));
  if (codeOnly) return codeOnly;

  return onField.find((v) => v.codes.length === 0 && v.source === 'question') ?? null;
}
